/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Customer Studio overview", description = "Build audiences from warehouse data, activate them across your tools, and run lifecycle programs without writing SQL.", lastUpdatedDate = new Date(1787702400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Customer Studio overview",
        description: "Build audiences from warehouse data, activate them across your tools, and run lifecycle programs without writing SQL.",
        lastUpdatedDate: "Wed Aug 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "How Customer Studio fits together",
          "depth": 0,
          "id": "how-customer-studio-fits-together"
        }, {
          "value": "Prepare the customer data",
          "depth": 1,
          "id": "prepare-the-customer-data"
        }, {
          "value": "Define who qualifies",
          "depth": 1,
          "id": "define-who-qualifies"
        }, {
          "value": "Decide what happens next",
          "depth": 1,
          "id": "decide-what-happens-next"
        }, {
          "value": "Understand what happened",
          "depth": 1,
          "id": "understand-what-happened"
        }, {
          "value": "Get started",
          "depth": 0,
          "id": "get-started"
        }, {
          "value": "For data teams and platform admins",
          "depth": 1,
          "id": "for-data-teams-and-platform-admins"
        }, {
          "value": "For marketers",
          "depth": 1,
          "id": "for-marketers"
        }, {
          "value": "How Customer Studio works with other Hightouch products",
          "depth": 0,
          "id": "how-customer-studio-works-with-other-hightouch-products"
        }, {
          "value": "Customer data",
          "depth": 1,
          "id": "customer-data"
        }, {
          "value": "Analysis and decisioning",
          "depth": 1,
          "id": "analysis-and-decisioning"
        }, {
          "value": "Measurement",
          "depth": 1,
          "id": "measurement"
        }, {
          "value": "Control access and delivery",
          "depth": 0,
          "id": "control-access-and-delivery"
        }, {
          "value": "Source compatibility",
          "depth": 0,
          "id": "source-compatibility"
        }, {
          "value": "Permissions",
          "depth": 0,
          "id": "permissions"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      ol: "ol"
    }, _provideComponents(), props.components), {BtOnly, Video, PylonLink} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!Video) _missingMdxReference("Video", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Customer Studio"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Audience"
            }), _jsx(_components.th, {
              children: "What this article helps you do"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Marketing"
            }), _jsx(_components.td, {
              children: "Build audiences, run journeys, and understand who you're reaching."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Data teams and admins"
            }), _jsx(_components.td, {
              children: "Prepare the customer data marketers and Agents work with, and configure access controls."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Use Customer Studio to build audiences and lifecycle programs from your warehouse data, without writing SQL."
        })
      }), "\n", _jsx(Video, {
        source: "https://cdn.sanity.io/files/pwmfmi47/production/fefc3b564a0f9e9ad5344813fbe05a82faeea94e.mp4",
        title: "Customer Studio overview"
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Customer Studio is where marketers use warehouse data to decide which customers to reach and what should happen next."
      }), "\n", _jsxs(_components.p, {
        children: ["Data teams make customer data available in Customer Studio, such as profile attributes, purchases, product activity, or other behaviors. Marketers use that data to build ", _jsx(_components.strong, {
          children: "audiences"
        }), ": groups of customers they want to reach, suppress, analyze, or include in a campaign."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can build an audience by choosing the characteristics and behaviors that define who should qualify, or ask the ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/customer-studio/agents",
          children: "Agent"
        }), " to help you get started. Before saving, you can review the audience criteria and preview the customers who match."]
      }), "\n", _jsx(_components.p, {
        children: "Once you've built an audience, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync it directly to a destination"
          }), " such as an ad platform, CRM, or messaging tool."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use it in a journey"
          }), " when a campaign needs multiple steps, delays, branches, or exit conditions."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Customer Studio reevaluates audiences as your warehouse data changes, so membership stays up to date."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/hightouch-audiences/overview/cs-audience-example.png",
          alt: "Audience builder combining an event, a property, and a custom trait condition"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "https://architecture.hightouch.com/?from=docs&product=customer-studio&utm_source=hightouch-docs&utm_medium=referral&utm_campaign=architecture-explorer&utm_content=customer-studio",
            children: _jsx(_components.strong, {
              children: "Explore the interactive architecture diagram"
            })
          }), " to see how Customer Studio works with your warehouse and destinations."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-customer-studio-fits-together",
        children: "How Customer Studio fits together"
      }), "\n", _jsx(_components.p, {
        children: "A typical workflow moves from customer data to an audience, then to a sync or journey."
      }), "\n", _jsx(_components.h3, {
        id: "prepare-the-customer-data",
        children: "Prepare the customer data"
      }), "\n", _jsxs(_components.p, {
        children: ["Data teams configure the ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "schema"
        }), " marketers use to build audiences. The schema connects a parent model, such as customers or accounts, with related records, events, and ", _jsx(_components.a, {
          href: "/customer-studio/traits",
          children: "traits"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["The schema determines which fields, events, and relationships marketers and ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " can use."]
      }), "\n", _jsx(_components.h3, {
        id: "define-who-qualifies",
        children: "Define who qualifies"
      }), "\n", _jsxs(_components.p, {
        children: ["An ", _jsx(_components.a, {
          href: "/customer-studio/usage",
          children: "audience"
        }), " defines which customers currently meet your targeting criteria."]
      }), "\n", _jsx(_components.p, {
        children: "For example, you might build an audience of customers who added an item to their cart but haven't purchased within 24 hours. You could use that audience to send a reminder, exclude those customers from another campaign, or analyze how they differ from customers who completed a purchase."
      }), "\n", _jsxs(_components.p, {
        children: ["Build the audience by adding conditions directly in the visual builder or asking the Agent to translate your intent into audience conditions. For prompting examples and guidance, see the ", _jsx(_components.a, {
          href: "/customer-studio/agents",
          children: "Audience builder prompting guide"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're starting with an open-ended question rather than a specific audience, use ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " to explore your data first. You can then build an audience from that analysis and continue working with it in Customer Studio."]
      }), "\n", _jsx(_components.h3, {
        id: "decide-what-happens-next",
        children: "Decide what happens next"
      }), "\n", _jsx(_components.p, {
        children: "Audiences can feed directly into syncs or become the starting population for a journey."
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.a, {
          href: "/customer-studio/syncs",
          children: "sync"
        }), " sends the current audience membership and customer attributes to a destination such as an ad platform, CRM, or messaging tool. Use a sync when the audience already contains the targeting logic you need and there is no multi-step campaign flow."]
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.a, {
          href: "/customer-studio/journeys",
          children: "journey"
        }), " coordinates what happens after someone qualifies. Journeys can wait between steps, branch based on customer behavior or attributes, remove members when they meet exit criteria, and trigger destination actions as the campaign progresses."]
      }), "\n", _jsx(_components.p, {
        children: "For example, a welcome journey might:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Start when a customer signs up."
        }), "\n", _jsx(_components.li, {
          children: "Wait two days."
        }), "\n", _jsx(_components.li, {
          children: "Send the customer to an email platform for an onboarding message."
        }), "\n", _jsx(_components.li, {
          children: "Check whether they activated."
        }), "\n", _jsx(_components.li, {
          children: "Send a reminder only to customers who haven't."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A journey decides who reaches each step and when. At a step, a journey either delivers a message itself or hands the work to another tool. Hightouch can send an email, SMS, or push message directly. Otherwise, a sync passes data to a downstream tool such as an ad platform, CRM, or messaging tool, which delivers the message or performs another action."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/hightouch-audiences/overview/cs-journey-example.png",
          alt: "Journey with an audience entry, a delay, a send, a hold-until step, and a rule-based branch"
        })
      }), "\n", _jsx(_components.h3, {
        id: "understand-what-happened",
        children: "Understand what happened"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/customer-studio/insights",
          children: "Audience insights"
        }), " to understand who is in an audience, how it is composed, and how it overlaps with other audiences."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/campaign-intelligence/overview",
          children: "Intelligence"
        }), " to measure campaign, treatment, and experiment performance."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started",
        children: "Get started"
      }), "\n", _jsx(_components.p, {
        children: "Customer Studio requires some initial setup from a data or platform team before marketers can build audiences and journeys."
      }), "\n", _jsx(_components.h3, {
        id: "for-data-teams-and-platform-admins",
        children: "For data teams and platform admins"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/sources/overview",
            children: "Connect your warehouse"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/destinations/overview",
            children: "Add the destinations"
          }), " your team plans to use."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/data-model",
            children: "Plan your data model"
          }), ", then ", _jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "configure your schema"
          }), " with the customer data marketers need."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: "Enable the Lightning sync engine"
          }), " if you plan to use features that require it, including journeys."]
        }), "\n", _jsxs(_components.li, {
          children: ["For large datasets, optionally ", _jsx(_components.a, {
            href: "/customer-studio/sampling",
            children: "configure sampling"
          }), " to speed up audience and trait previews."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The Customer Studio schema also gives ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " structured customer data to work with. If Agents need warehouse data that doesn't belong in the audience-building schema, add ", _jsx(_components.a, {
          href: "/customer-studio/general-models",
          children: "general models"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "for-marketers",
        children: "For marketers"
      }), "\n", _jsxs(_components.p, {
        children: ["Start by ", _jsx(_components.a, {
          href: "/customer-studio/usage",
          children: "creating an audience"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Once you're comfortable defining and validating audiences, choose how the campaign should use them:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/syncs",
            children: "Sync audiences"
          }), " directly to destinations."]
        }), "\n", _jsxs(_components.li, {
          children: ["Build ", _jsx(_components.a, {
            href: "/customer-studio/journeys",
            children: "journeys"
          }), " for campaigns that need timing, branching, or multiple steps."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use ", _jsx(_components.a, {
            href: "/customer-studio/insights",
            children: "Audience insights"
          }), " to understand who qualifies."]
        }), "\n", _jsxs(_components.li, {
          children: ["Save repeatable audience logic with ", _jsx(_components.a, {
            href: "/customer-studio/templates",
            children: "templates"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-customer-studio-works-with-other-hightouch-products",
        children: "How Customer Studio works with other Hightouch products"
      }), "\n", _jsx(_components.p, {
        children: "Customer Studio builds on other parts of Hightouch when your implementation needs richer customer data, broader analysis, or additional campaign optimization."
      }), "\n", _jsx(_components.h3, {
        id: "customer-data",
        children: "Customer data"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Warehouse models"
          }), " provide the customer, account, transaction, and other data you organize in your ", _jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "Customer Studio schema"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/identity-resolution/overview",
              children: "Identity Resolution"
            })
          }), " can provide a Golden Record when you need audiences built from resolved customer profiles."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/events/overview",
              children: "Events"
            })
          }), " provide behavioral data such as page views, purchases, and app activity for audience conditions and journey triggers."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "analysis-and-decisioning",
        children: "Analysis and decisioning"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " for open-ended analysis when you're still deciding which customers to target or what action to take."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/ai-decisioning/overview",
          children: "AI Decisioning"
        }), " when you want Hightouch to adaptively choose the best message, channel, and timing for each eligible person."]
      }), "\n", _jsx(_components.h3, {
        id: "measurement",
        children: "Measurement"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/campaign-intelligence/overview",
          children: "Intelligence"
        }), " to measure campaign and experiment outcomes."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "control-access-and-delivery",
        children: "Control access and delivery"
      }), "\n", _jsx(_components.p, {
        children: "Customer Studio includes controls for which customer data teams can work with and what Hightouch can send downstream."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/customer-studio/subsets",
              children: "Subsets"
            })
          }), " restrict which records particular users or groups can work with."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/customer-studio/data-masking",
              children: "Data masking"
            })
          }), " protects sensitive values."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/customer-studio/destination-rules",
              children: "Destination rules"
            })
          }), " filter records before they are sent to specific destinations without changing audience membership."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/customer-studio/onetrust",
              children: "OneTrust integration"
            })
          }), " applies consent data to activation workflows."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/customer-studio/schema-setup",
              children: "Schema Git Sync"
            })
          }), " manages Customer Studio schema configuration through Git."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Destination rules, subsets, and consent controls apply at different points in the workflow, so a customer can qualify for an audience but still be excluded before data reaches a destination."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "source-compatibility",
        children: "Source compatibility"
      }), "\n", _jsx(_components.p, {
        children: "Customer Studio supports the following warehouse sources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Snowflake"
        }), "\n", _jsx(_components.li, {
          children: "Databricks"
        }), "\n", _jsx(_components.li, {
          children: "Google BigQuery"
        }), "\n", _jsx(_components.li, {
          children: "Amazon Redshift*"
        }), "\n", _jsx(_components.li, {
          children: "Trino"
        }), "\n", _jsx(_components.li, {
          children: "Amazon Athena"
        }), "\n", _jsx(_components.li, {
          children: "Azure Synapse"
        }), "\n", _jsx(_components.li, {
          children: "Microsoft Fabric"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["* Customer Studio may not work with ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/redshift/latest/dg/c-getting-started-using-spectrum.html",
            children: "Amazon Redshift Spectrum"
          }), " due to its ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/redshift/latest/dg/nested-data-restrictions.html",
            children: "limitations around nested data"
          }), ". To mitigate these, you can query a ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/redshift/latest/dg/materialized-view-overview.html",
            children: "materialized view"
          }), " during your ", _jsx(_components.a, {
            href: "/customer-studio/schema#create-the-parent-model",
            children: "parent model setup"
          }), "."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Beyond baseline support, some capabilities have additional source requirements, including ", _jsx(_components.a, {
          href: "/customer-studio/journeys",
          children: "journeys"
        }), ", ", _jsx(_components.a, {
          href: "/customer-studio/predictive-traits",
          children: "predictive traits"
        }), ", and ", _jsx(_components.a, {
          href: "/customer-studio/audience-snapshots",
          children: "audience snapshots"
        }), ". Check the requirements for the feature you plan to use, or ", _jsx(PylonLink, {
          children: "contact support"
        }), " to confirm compatibility with your warehouse."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsx(_components.p, {
        children: "Access to Customer Studio features is controlled through custom roles."
      }), "\n", _jsxs(_components.p, {
        children: ["Admins configure these grants in the ", _jsx(_components.strong, {
          children: "Models"
        }), " section of the custom role builder: ", _jsx(_components.strong, {
          children: "View parent model data"
        }), ", ", _jsx(_components.strong, {
          children: "Configure audiences & syncs"
        }), ", ", _jsx(_components.strong, {
          children: "Manage traits"
        }), ", ", _jsx(_components.strong, {
          children: "Configure journeys"
        }), ", and ", _jsx(_components.strong, {
          children: "Publish journeys"
        }), ". The trait and journey grants appear only when those features are enabled."]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/workspace-management/roles#models",
          children: "Roles"
        }), " for the full list of grants."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
